import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../common/Button";
import { FormLayout } from "../common/FormLayout";
import { PasswordField } from "../common/PasswordField";

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_URL_SERVER_DATA}/verify-expiration-reset-password`,
          {
            method: "POST",
            headers: {
              "Content-type": "application/json",
            },
            body: JSON.stringify({ token }),
          }
        );

        if (response.status === 410) {
          setError(
            "Le lien de réinitialisation a expiré. Vous allez être redirigé pour faire une nouvelle demande."
          );
          setTimeout(() => {
            navigate("/forgot-password");
          }, 3000);
        } else if (response.status === 500) {
          setError("Une erreur est survenue. Veuillez réessayer plus tard.");
        } else if (!response.ok) {
          setError("Une erreur est survenue. Veuillez réessayer plus tard.");
        }
      } catch (error) {
        console.error("Erreur lors de la vérification du token:", error);
      }
    };

    verifyToken();
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_SERVER_DATA}/create-password`,
        {
          method: "POST",
          headers: {
            "Content-type": "application/json",
          },
          body: JSON.stringify({
            password,
            token,
          }),
        }
      );

      if (response.ok) {
        navigate("/login");
      } else {
        const data = await response.json();
        setError(
          data.message || "Une erreur est survenue. Veuillez réessayer."
        );
      }
    } catch (error) {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout title="Nouveau mot de passe">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <PasswordField
          label="Nouveau mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading}
          showRules
        />

        <PasswordField
          label="Confirmer le mot de passe"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isLoading}
        />

        <Button
          type="submit"
          size="full"
          disabled={!password || !confirmPassword || isLoading}
          className={
            !password || !confirmPassword || isLoading
              ? "opacity-50 cursor-not-allowed"
              : ""
          }
        >
          {isLoading ? "MODIFICATION EN COURS..." : "MODIFIER MON MOT DE PASSE"}
        </Button>

        <div className="mt-6 text-center">
          <a href="/login" className="block text-tertiary hover:underline">
            Retour à la connexion
          </a>
        </div>
      </form>
    </FormLayout>
  );
};
